/**
 * Internationalisation module.
 * Set REACT_APP_LANG=tr in the environment for Turkish. Defaults to English.
 *
 * REACT_APP_HALAL_MODE=true    — shows the halal menu (see `productDefs.ts`).
 * REACT_APP_FREE_MODE=true     — hides all prices and skips the payment step.
 *
 * Each of these is an `.env` *default* that can be overridden at runtime via the
 * `localStorage` settings layer — see `settings.ts`. Values are read through it.
 */
import { useSyncExternalStore } from 'react';
import { getBool, getEnum, saveSettings } from './settings';

/** When true, prices are hidden and the payment page is skipped. */
export const FREE_MODE = getBool('freeMode');

/** Active UI colour theme.  'rush' = green/black tech (default).  'luxe' = warm gold luxury. */
export const THEME = getEnum<'rush' | 'luxe'>('theme', ['rush', 'luxe'], 'rush');

export interface Translations {
  // ── SelectionPage ──────────────────────────────────────────────
  chooseYourDrink: string;
  tapToContinue: string;
  tapToCustomize: string;
  selectDrink: string;
  /** Badge on drinks that contain alcohol. */
  alcoholicLabel: string;
  /** Badge on drinks that do not contain alcohol. */
  nonAlcoholicLabel: string;
  /** Aria label for the drink description info control. */
  drinkInfoAria: string;

  // product name/desc keyed by PRODUCT_DEFS id
  productNames: Record<string, string>;
  productDescs: Record<string, string>;

  /**
   * Display names for products reported by the machine backend, keyed by the
   * backend's English `name` (its `product_id`s are UUIDs, so they can't key
   * a static map). Unknown names fall back to the backend name unchanged.
   */
  machineProductNames: Record<string, string>;

  // ── Header ─────────────────────────────────────────────────────
  back: string;

  // ── CustomizePage ──────────────────────────────────────────────
  mixerSection: string;
  sweetnessSection: string;
  addOnsSection: string;

  /** Display labels for mixer keys ('None' | 'SourCherryJuice' | 'Tonic' | 'Ginger Beer' | 'IcedTea'). */
  mixerLabels: Record<string, string>;
  /** Word appended after a mixer name in summary chips, e.g. "Sour Cherry Juice Mixer". */
  mixerSuffix: string;

  sweetnessDry: string;
  sweetnessBalanced: string;
  sweetnessLevel: (n: number) => string;

  /** Display labels for extra IDs. */
  extraLabels: Record<string, string>;

  // ── MenuTypePage ───────────────────────────────────────────────
  chooseExperience: string;
  mainMenuTitle: string;
  mainMenuDesc: string;
  customMixTitle: string;
  customMixDesc: string;
  aiPickTitle: string;
  aiPickDesc: string;
  aiPickIntroWelcome: string;
  aiPickIntroScanReady: string;
  aiPickContinue: string;
  aiPickRememberVibe: string;
  aiPickDrinkPreferencePrompt: string;
  aiPickAlcoholOption: string;
  aiPickAlcoholOptionDesc: string;
  aiPickAlcoholFreeOption: string;
  aiPickAlcoholFreeOptionDesc: string;
  aiPickLoadingDrinkOptions: string;
  aiPickOptionUnavailable: string;
  aiPickAgeRestricted: string;
  aiPickNoMatchingDrinks: string;
  aiPickStartingCamera: string;
  aiPickLoadingFaceDetection: string;
  aiPickStayInFrame: string;
  aiPickHoldStillCapturing: string;
  aiPickHoldStillAlmostReady: string;
  aiPickHoldHeadSteady: string;
  aiPickLookAtCamera: string;
  aiPickPreparingDrinkForYou: string;
  aiPickSuggestDrinks: string;
  /**
   * Mood-aware variant of `aiPickSuggestDrinks`. The argument is a localized
   * emotion adjective (e.g. "mutlu" / "happy") that slots into the sentence.
   * Used when the backend returned an emotion we have a label for.
   */
  aiPickSuggestDrinksForMood: (mood: string) => string;
  /**
   * Localized labels for the emotion keys the ML backend produces (after
   * `normalize_emotion_label` in `rush_ml_backend.py`): happy, sad, neutral,
   * surprise, tired, angry, disgust, fear.
   */
  aiPickEmotionLabels: Record<string, string>;
  aiPickPickOneToContinue: string;
  aiPickPreparingOptions: string;
  aiPickPreparingLabel: string;
  aiPickPreparingLoader: string;
  aiPickAnalyzingMood: string;
  aiPickPreparingSuggestions: string;
  aiPickErrorTitle: string;
  aiPickTapToDismiss: string;
  comingSoon: string;

  // ── CustomMixPage ──────────────────────────────────────────────
  customMixHeading: string;
  customMixSub: string;
  totalMl: (ml: number) => string;
  maxMlLabel: string;
  confirmMix: string;
  mlUnit: string;
  customMixProductName: string;

  // ── UserFormPage ───────────────────────────────────────────────
  formTitle: string;
  formNamePlaceholder: string;
  formAgePlaceholder: string;
  formGenderLabel: string;
  formGenderMale: string;
  formGenderFemale: string;
  formGenderOther: string;
  formSubmit: string;

  // ── QROrderPage ────────────────────────────────────────────────
  qrTitle: string;
  qrInstruction: string;
  qrOrderSummary: (name: string) => string;
  /** Small label shown above the drink name on the QR page. */
  qrOrderLabel: string;
  qrStartOver: string;
  /** CTA that sends the guest to the post-pour survey. */
  qrRateDrink: string;

  // ── SurveyPage ─────────────────────────────────────────────────
  surveyTitle: string;
  surveySubtitle: string;
  surveyPickDrink: string;
  surveyPickDrinkPlaceholder: string;
  surveyLoadingDrinks: string;
  surveyDrinkRating: string;
  surveyServiceRating: string;
  surveyRecommend: string;
  surveyYes: string;
  surveyNo: string;
  surveyAge: string;
  surveyAgePlaceholder: string;
  surveyNationality: string;
  surveyNationalityPlaceholder: string;
  surveyWantMoreTech: string;
  surveyComment: string;
  surveyCommentPlaceholder: string;
  surveyImprovement: string;
  surveyImprovementPlaceholder: string;
  surveySubmit: string;
  surveySubmitting: string;
  surveySkip: string;
  surveyThanks: string;
  surveyThanksSub: string;

  // ── AiPickMenuPage ─────────────────────────────────────────────
  aiPickFaceDetection: string;
  aiPickFaceDetectionDesc: string;
  aiPickChat: string;
  aiPickChatDesc: string;

  // ── ChatPage ───────────────────────────────────────────────────
  chatWelcome: string;
  chatConnecting: string;
  chatPlaceholder: string;
  chatError: string;
  chatSuggestions: string[];
  /** Fallback user name sent to the chat backend when no user form was filled. */
  chatGuestName: string;
  chatRecommendedDrink: string;
  chatCustomBlend: string;
  chatLetsOrder: string;
  chatRateLimited: string;
  chatSessionExpiredTitle: string;
  chatSessionExpiredBody: string;
  chatStartAgain: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// English
// ─────────────────────────────────────────────────────────────────────────────
const en: Translations = {
  chooseYourDrink: 'Choose Your Drink',
  tapToContinue: '',
  tapToCustomize: 'Tap a drink to continue',
  selectDrink: 'Select Drink',
  alcoholicLabel: 'Alcoholic',
  nonAlcoholicLabel: 'Non-alcoholic',
  drinkInfoAria: 'Drink description',

  productNames: {
    'cool-lime':                'Cool Lime',
    'iced-coffee':              'Iced Coffee',
    'butterfly-pea-flower-tea': 'Butterfly Pea Flower Tea',
    'lemonade':                 'Lemonade',
    'vishne-juice':             'Sour Cherry Juice',
    'iced-tea':                 'Peach Iced Tea',
    'lime':                     'Lime',
    'water':                    'Water',
    'rush-cocktail':            'Rush Cocktail',
  },
  machineProductNames: {
    'Cola Classic':    'Cola Classic',
    'Orange Juice':    'Orange Juice',
    'Sparkling Water': 'Sparkling Water',
    'Energy Boost':    'Energy Boost',
  },
  productDescs: {
    'cool-lime':                'Bright lime cooler with a crisp finish',
    'iced-coffee':              'Cold brew over ice, smooth and energizing',
    'butterfly-pea-flower-tea': 'Floral tea with a calming color-shift ritual',
    'lemonade':                 'Fresh lemon refreshment, light and uplifting',
    'vishne-juice':             'Tart sour cherry juice, deep and refreshing',
    'iced-tea':                 'Chilled peach iced tea with a clean finish',
    'lime':                     'Fresh lime',
    'water':                    'Pure and refreshing water',
    'rush-cocktail':            'Vibrant and balanced mix',
  },

  back: 'Back',

  mixerSection: '🥤 Mixer',
  sweetnessSection: '🍯 Sweetness',
  addOnsSection: '✨ Add-ons',

  mixerLabels:  { None: 'None', SourCherryJuice: 'Sour Cherry Juice', Tonic: 'Tonic', 'Ginger Beer': 'Ginger Beer', IcedTea: 'Peach Iced Tea' },
  mixerSuffix: 'Mixer',

  sweetnessDry: 'Dry',
  sweetnessBalanced: 'Balanced',
  sweetnessLevel: (n) => `Sweet Lv.${n}`,

  extraLabels: {
    'extra-shot': 'Extra Shot',
    lime:   'Fresh Lime',
    mint:   'Mint Burst',
    energy: 'Energy Kick',
    smoke:  'Smoked Rim',
  },

  chooseExperience: 'Choose Your Experience',
  mainMenuTitle: 'Main Menu',
  mainMenuDesc: 'Browse our full selection of drinks',
  customMixTitle: 'Customize Your Drink',
  customMixDesc: 'Build your own blend',
  aiPickTitle: 'RUSH-TENDER Picks for Me',
  aiPickDesc: 'Let RUSH-TENDER suggest the perfect drink',
  aiPickIntroWelcome: "Welcome! I'm RUSH-TENDER, your AI bartender — I'll do my best to find you the perfect drink.",
  aiPickIntroScanReady: "For me to suggest you a drink, I'd like to see your current mood and I will be scanning your face. Click on this button to continue.",
  aiPickContinue: 'Continue',
  aiPickRememberVibe: 'Remember my drink vibe for faster recommendations next time',
  aiPickDrinkPreferencePrompt: 'Before I scan your face, would you like an alcoholic or non-alcoholic drink?',
  aiPickAlcoholOption: 'Alcoholic',
  aiPickAlcoholOptionDesc: 'Recommend an alcoholic drink',
  aiPickAlcoholFreeOption: 'Non-alcoholic',
  aiPickAlcoholFreeOptionDesc: 'Recommend a drink without alcohol',
  aiPickLoadingDrinkOptions: 'Loading drink options…',
  aiPickOptionUnavailable: 'Not available on this machine',
  aiPickAgeRestricted: 'Not available for your age',
  aiPickNoMatchingDrinks: 'No available drinks match that choice. Please go back and choose another option.',
  aiPickStartingCamera: 'Starting camera…',
  aiPickLoadingFaceDetection: 'Loading face detection…',
  aiPickStayInFrame: 'Stay in frame — countdown starts automatically in a few seconds',
  aiPickHoldStillCapturing: 'Hold still — capturing…',
  aiPickHoldStillAlmostReady: 'Hold still — almost ready…',
  aiPickHoldHeadSteady: 'Hold your head steady',
  aiPickLookAtCamera: 'Look at the camera',
  aiPickPreparingDrinkForYou: 'I am preparing the drink for you.',
  aiPickSuggestDrinks: "I'd like to suggest 2 drinks for you.",
  aiPickSuggestDrinksForMood: (mood) =>
    `You seem to be feeling a bit ${mood} — these are what I had in mind for you.`,
  aiPickEmotionLabels: {
    happy: 'happy',
    sad: 'down',
    neutral: 'relaxed',
    surprise: 'surprised',
    tired: 'tired',
    angry: 'tense',
    disgust: 'off-mood',
    fear: 'uneasy',
  },
  aiPickPickOneToContinue: 'Pick one to continue',
  aiPickPreparingOptions: 'I am still preparing your options.',
  aiPickPreparingLabel: 'Preparing...',
  aiPickPreparingLoader: 'Preparing your drink...',
  aiPickAnalyzingMood: 'Analyzing your mood…',
  aiPickPreparingSuggestions: 'Preparing drink suggestions…',
  aiPickErrorTitle: 'Something went wrong',
  aiPickTapToDismiss: 'Tap to dismiss',
  comingSoon: 'Coming Soon',

  customMixHeading: 'Build Your Mix',
  customMixSub: 'Adjust each drink — max 200 ml total',
  totalMl: (ml) => `${ml} ml / 200 ml`,
  maxMlLabel: '200 ml',
  confirmMix: 'Confirm Mix',
  mlUnit: 'ml',
  customMixProductName: 'Custom Mix',

  formTitle: 'Welcome to RUSH',
  formNamePlaceholder: 'Your name',
  formAgePlaceholder: 'Your age',
  formGenderLabel: 'Gender',
  formGenderMale: 'Male',
  formGenderFemale: 'Female',
  formGenderOther: 'Prefer not to say',
  formSubmit: 'Continue to Menu',

  qrTitle: 'Your Order is Ready',
  qrInstruction: 'Scan this QR code on the machine to get your drink',
  qrOrderSummary: (name) => `Order: ${name}`,
  qrOrderLabel: 'Your order',
  qrStartOver: 'Start Over',
  qrRateDrink: 'Rate Your Drink',

  surveyTitle: 'Quick Feedback',
  surveySubtitle: 'Optional — helps us pour better next time',
  surveyPickDrink: 'Which drink did you have?',
  surveyPickDrinkPlaceholder: 'Select a drink…',
  surveyLoadingDrinks: 'Loading drinks…',
  surveyDrinkRating: 'How was your drink?',
  surveyServiceRating: 'How was the ordering & pouring experience?',
  surveyRecommend: 'Would you recommend this drink?',
  surveyYes: 'Yes',
  surveyNo: 'No',
  surveyAge: 'What is your age?',
  surveyAgePlaceholder: 'Age',
  surveyNationality: 'What is your nationality?',
  surveyNationalityPlaceholder: 'Nationality',
  surveyWantMoreTech: 'Would you like to see more next-generation technologies like this?',
  surveyComment: 'Anything else?',
  surveyCommentPlaceholder: 'Optional comment…',
  surveyImprovement: 'Is there anything you would suggest we improve?',
  surveyImprovementPlaceholder: 'Optional suggestion…',
  surveySubmit: 'Submit',
  surveySubmitting: 'Sending…',
  surveySkip: 'Skip',
  surveyThanks: 'Thanks!',
  surveyThanksSub: 'Enjoy your drink',

  aiPickFaceDetection: 'Scan My Mood',
  aiPickFaceDetectionDesc: 'Open the camera — RUSH-TENDER reads your mood from your face and picks the perfect drink',
  aiPickChat: 'Describe My Vibe',
  aiPickChatDesc: 'Chat with RUSH-TENDER about what you feel like and get a drink recommendation',

  chatWelcome:
    "Hey! I'm RUSH-TENDER, your drink guide. Tell me what you're in the mood for and I'll find your perfect drink. 🍹",
  chatConnecting: 'Connecting…',
  chatPlaceholder: 'Type your message…',
  chatError: 'Sorry, I could not reach RUSH-TENDER right now. Please try again.',
  chatSuggestions: [
    'Something refreshing 🍋',
    'Surprise me ✨',
    'I want something sweet',
    'Low caffeine, please',
  ],
  chatGuestName: 'Guest',
  chatRecommendedDrink: 'Recommended Drink',
  chatCustomBlend: 'Custom Blend',
  chatLetsOrder: "Let's Order",
  chatRateLimited: "You're sending messages too fast — wait a moment and try again.",
  chatSessionExpiredTitle: 'Session timed out',
  chatSessionExpiredBody: 'Your chat session expired after 3 minutes of inactivity.',
  chatStartAgain: 'Start Again',
};

// ─────────────────────────────────────────────────────────────────────────────
// Turkish
// ─────────────────────────────────────────────────────────────────────────────
const tr: Translations = {
  chooseYourDrink: 'İçeceğinizi Seçin',
  tapToContinue: '',
  tapToCustomize: 'Devam etmek için bir içeceğe dokunun',
  selectDrink: 'Seç',
  alcoholicLabel: 'Alkollü',
  nonAlcoholicLabel: 'Alkolsüz',
  drinkInfoAria: 'İçecek açıklaması',

  productNames: {
    'butterfly-pea-flower-tea': 'Mavi Kelebek Çayı',
    'iced-tea':                 'Soğuk Şeftali Çayı',
    'water':                    'Su',
    'lime':                     'Misket Limonu',
    'iced-coffee':              'Soğuk Kahve',
    'vishne-juice':             'Vişne Suyu',
    'rush-cocktail':            'Rush Kokteyl',
    'cool-lime':                'Ferah Misket Limon',
    'lemonade':                 'Limonata',
  },
  machineProductNames: {
    'Cola Classic':    'Klasik Kola',
    'Orange Juice':    'Portakal Suyu',
    'Sparkling Water': 'Maden Suyu',
    'Energy Boost':    'Enerji İçeceği',
  },
  productDescs: {
    'butterfly-pea-flower-tea': 'Rahatlatıcı renk değiştiren bitki çayı',
    'iced-tea':                 'Serinletici soğuk şeftali çayı',
    'water':                    'Saf ve ferahlatıcı su',
    'lime':                     'Taze misket limonu',
    'iced-coffee':              'Buzlu soğuk demleme kahve',
    'vishne-juice':             'Mayhoş ve ferahlatıcı vişne suyu',
    'rush-cocktail':            'Canlı ve dengeli bir karışım',
    'cool-lime':                'Canlandırıcı ferah limon içeceği',
    'lemonade':                 'Hafif ve ferahlatıcı limonata',
  },

  back: 'Geri',

  mixerSection: '🥤 Mixer',
  sweetnessSection: '🍯 Tatlılık',
  addOnsSection: '✨ Ekstralar',

  mixerLabels:  { None: 'Yok', SourCherryJuice: 'Vişne Suyu', Tonic: 'Tonik', 'Ginger Beer': 'Zencefil Birası', IcedTea: 'Soğuk Şeftali Çayı' },
  mixerSuffix: 'Karışımı',

  sweetnessDry: 'Kuru',
  sweetnessBalanced: 'Dengeli',
  sweetnessLevel: (n) => `Tatlı Sv.${n}`,

  extraLabels: {
    'extra-shot': 'Ekstra Shot',
    lime:   'Taze Limon',
    mint:   'Nane',
    energy: 'Enerji',
    smoke:  'Dumanlı Kenar',
  },

  chooseExperience: 'Deneyiminizi Seçin',
  mainMenuTitle: 'Ana Menü',
  mainMenuDesc: 'Tüm içeceklerimize göz atın',
  customMixTitle: 'İçeceğinizi Özelleştirin',
  customMixDesc: 'Kendi karışımınızı oluşturun',
  aiPickTitle: 'RUSH-TENDER Seçsin',
  aiPickDesc: 'RUSH-TENDER sizin için mükemmel içeceği önersin',
  aiPickIntroWelcome: 'Hoş geldiniz! Ben RUSH-TENDER, yapay zekâ barmeniniz — size en uygun içeceği bulmak için elimden geleni yapacağım.',
  aiPickIntroScanReady: 'Size bir içecek önerebilmem için o anki ruh halinizi görmem gerekiyor ve yüzünüzü tarayacağım. Devam etmek için bu düğmeye dokunun.',
  aiPickContinue: 'Devam Et',
  aiPickRememberVibe: 'Bir dahaki sefere daha hızlı öneri için içecek zevkimi hatırla',
  aiPickDrinkPreferencePrompt: 'Yüzünüzü taramadan önce alkollü mü yoksa alkolsüz mü bir içecek istersiniz?',
  aiPickAlcoholOption: 'Alkollü',
  aiPickAlcoholOptionDesc: 'Alkollü bir içecek öner',
  aiPickAlcoholFreeOption: 'Alkolsüz',
  aiPickAlcoholFreeOptionDesc: 'Alkolsüz bir içecek öner',
  aiPickLoadingDrinkOptions: 'İçecek seçenekleri yükleniyor…',
  aiPickOptionUnavailable: 'Bu makinede mevcut değil',
  aiPickAgeRestricted: 'Yaşınız için uygun değil',
  aiPickNoMatchingDrinks: 'Bu seçeneğe uygun içecek bulunamadı. Lütfen geri dönüp başka bir seçenek seçin.',
  aiPickStartingCamera: 'Kamera başlatılıyor…',
  aiPickLoadingFaceDetection: 'Yüz algılama yükleniyor…',
  aiPickStayInFrame: 'Kadrajda kalın — geri sayım birkaç saniye içinde otomatik başlayacak',
  aiPickHoldStillCapturing: 'Kıpırdamayın — görüntü alınıyor…',
  aiPickHoldStillAlmostReady: 'Kıpırdamayın — neredeyse hazır…',
  aiPickHoldHeadSteady: 'Başınızı sabit tutun',
  aiPickLookAtCamera: 'Kameraya bakın',
  aiPickPreparingDrinkForYou: 'İçeceğinizi hazırlıyorum.',
  aiPickSuggestDrinks: 'Size 2 içecek önermek istiyorum.',
  aiPickSuggestDrinksForMood: (mood) =>
    `Biraz ${mood} hissediyor gibisiniz; sizin için bunları düşündüm.`,
  aiPickEmotionLabels: {
    happy: 'mutlu',
    sad: 'üzgün',
    neutral: 'rahat',
    surprise: 'şaşkın',
    tired: 'yorgun',
    angry: 'gergin',
    disgust: 'keyifsiz',
    fear: 'tedirgin',
  },
  aiPickPickOneToContinue: 'Devam etmek için birini seçin',
  aiPickPreparingOptions: 'Seçeneklerinizi hâlâ hazırlıyorum.',
  aiPickPreparingLabel: 'Hazırlanıyor...',
  aiPickPreparingLoader: 'İçeceğiniz hazırlanıyor...',
  aiPickAnalyzingMood: 'Ruh haliniz analiz ediliyor…',
  aiPickPreparingSuggestions: 'İçecek önerileri hazırlanıyor…',
  aiPickErrorTitle: 'Bir şeyler yanlış gitti',
  aiPickTapToDismiss: 'Kapatmak için dokunun',
  comingSoon: 'Çok Yakında',

  customMixHeading: 'Karışımınızı Oluşturun',
  customMixSub: 'Her içeceği ayarlayın — toplam max 200 ml',
  totalMl: (ml) => `${ml} ml / 200 ml`,
  maxMlLabel: '200 ml',
  confirmMix: 'Karışımı Onayla',
  mlUnit: 'ml',
  customMixProductName: 'Özel Karışım',

  formTitle: "RUSH'a Hoş Geldiniz",
  formNamePlaceholder: 'Adınız',
  formAgePlaceholder: 'Yaşınız',
  formGenderLabel: 'Cinsiyet',
  formGenderMale: 'Erkek',
  formGenderFemale: 'Kadın',
  formGenderOther: 'Belirtmek istemiyorum',
  formSubmit: 'Menüye Geç',

  qrTitle: 'Siparişiniz Hazır',
  qrInstruction: 'İçeceğinizi almak için bu QR kodu makineye okutun',
  qrOrderSummary: (name) => `Sipariş: ${name}`,
  qrOrderLabel: 'Siparişiniz',
  qrStartOver: 'Yeniden Başla',
  qrRateDrink: 'İçeceğinizi Değerlendirin',

  surveyTitle: 'Hızlı Geri Bildirim',
  surveySubtitle: 'İsteğe bağlı — bir sonraki dökümü daha iyi yapmamıza yardımcı olur',
  surveyPickDrink: 'Hangi içeceği aldınız?',
  surveyPickDrinkPlaceholder: 'İçecek seçin…',
  surveyLoadingDrinks: 'İçecekler yükleniyor…',
  surveyDrinkRating: 'İçeceğiniz nasıldı?',
  surveyServiceRating: 'Sipariş ve döküm deneyimi nasıldı?',
  surveyRecommend: 'Bu içeceği tavsiye eder misiniz?',
  surveyYes: 'Evet',
  surveyNo: 'Hayır',
  surveyAge: 'Yaşınız nedir?',
  surveyAgePlaceholder: 'Yaş',
  surveyNationality: 'Uyruğunuz nedir?',
  surveyNationalityPlaceholder: 'Uyruk',
  surveyWantMoreTech: 'Bunun gibi yeni nesil teknolojileri daha fazla görmek ister misiniz?',
  surveyComment: 'Eklemek istediğiniz bir şey var mı?',
  surveyCommentPlaceholder: 'İsteğe bağlı yorum…',
  surveyImprovement: 'Geliştirmemizi önerdiğiniz bir şey var mı?',
  surveyImprovementPlaceholder: 'İsteğe bağlı öneri…',
  surveySubmit: 'Gönder',
  surveySubmitting: 'Gönderiliyor…',
  surveySkip: 'Geç',
  surveyThanks: 'Teşekkürler!',
  surveyThanksSub: 'Afiyet olsun',

  aiPickFaceDetection: 'Ruh Halimi Tara',
  aiPickFaceDetectionDesc: 'Kamerayı aç — RUSH-TENDER yüzünden ruh halini okuyup sana mükemmel içeceği seçer',
  aiPickChat: 'Modumu Anlat',
  aiPickChatDesc: 'Ne istediğini RUSH-TENDER ile konuş, sana içecek önerisi versin',

  chatWelcome:
    'Merhaba! Ben RUSH-TENDER, içecek rehberin. Canın ne çekiyor söyle, sana mükemmel içeceği bulayım. 🍹',
  chatConnecting: 'Bağlanıyor…',
  chatPlaceholder: 'Mesajını yaz…',
  chatError: "Üzgünüm, şu anda RUSH-TENDER'a ulaşamadım. Lütfen tekrar dene.",
  chatSuggestions: [
    'Ferahlatıcı bir şey 🍋',
    'Beni şaşırt ✨',
    'Tatlı bir şey istiyorum',
    'Az kafeinli olsun',
  ],
  chatGuestName: 'Misafir',
  chatRecommendedDrink: 'Önerilen İçecek',
  chatCustomBlend: 'Özel Karışım',
  chatLetsOrder: 'Sipariş Verelim',
  chatRateLimited: 'Mesajları çok hızlı gönderiyorsun — biraz bekleyip tekrar dene.',
  chatSessionExpiredTitle: 'Oturum zaman aşımına uğradı',
  chatSessionExpiredBody: 'Sohbet oturumun 3 dakika işlem yapılmadığı için sona erdi.',
  chatStartAgain: 'Yeniden Başla',
};

// ─────────────────────────────────────────────────────────────────────────────
// Export
// ─────────────────────────────────────────────────────────────────────────────
export type Lang = 'en' | 'tr';

const LANGS: Record<Lang, Translations> = { en, tr };

/** Current language. Starts from the persisted/default `lang` setting. */
let currentLang: Lang = getEnum<Lang>('lang', ['en', 'tr'], 'en');

/**
 * Active translation table. This is a *live binding*: `setLanguage` reassigns
 * it, and because components read `t.x` at render time and the app re-renders
 * on a language change (see `useLang`, mounted in the router Shell), the new
 * language appears instantly with no page reload.
 *
 * Caveat: anything that captures `t.x` once at *module load* (a top-level
 * `const`) is frozen in the initial language — read `t.x` at render time, or
 * call a small helper, instead.
 */
export let t: Translations = LANGS[currentLang];

/** @deprecated Initial language at load only. Use `getLang()` / `useLang()` for the live value. */
export const LANG: Lang = currentLang;

/** The live current language. */
export function getLang (): Lang {
  return currentLang;
}

const langListeners = new Set<() => void>();

/** Switch language instantly and persist the choice (no reload). */
export function setLanguage (lang: Lang): void {
  if (lang === currentLang) return;
  currentLang = lang;
  t = LANGS[lang];
  saveSettings({ lang });
  langListeners.forEach((cb) => cb());
}

function subscribeLang (cb: () => void): () => void {
  langListeners.add(cb);
  return () => { langListeners.delete(cb); };
}

/**
 * Re-render a component whenever the language changes, returning the live
 * language. Mounted near the app root (the router Shell) so the whole tree
 * re-renders and re-reads `t` on a switch.
 */
export function useLang (): Lang {
  return useSyncExternalStore(subscribeLang, getLang, getLang);
}
